import { isReferralCode, normalizeReferralCode } from '@tv-casino/shared';

const PLAYER_ID = 'tvcasino.playerId';
const PLAYER_NAME = 'tvcasino.playerName';
const PLAYER_ROOM = 'tvcasino.playerRoom';
const DISPLAY_TOKEN = 'tvcasino.displayToken';
const DISPLAY_CODE = 'tvcasino.displayCode';
const DISPLAY_CASINO = 'tvcasino.displayCasino';
const DISPLAY_TABLE = 'tvcasino.displayTable';
const LAST_VENUE = 'tvcasino.lastCasino';
const STATS_WINDOW = 'tvcasino.statsWindow';
const REFERRAL = 'tvcasino.referral';

const read = (store: Storage, key: string): string | null => {
  try {
    return store.getItem(key);
  } catch {
    return null; // Safari private mode.
  }
};

const write = (store: Storage, key: string, value: string | null) => {
  try {
    if (value === null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    /* ignore */
  }
};

/** Seat identity survives a phone refresh. */
export const playerIdentity = {
  get: () => ({
    playerId: read(localStorage, PLAYER_ID),
    name: read(localStorage, PLAYER_NAME),
    code: read(localStorage, PLAYER_ROOM),
  }),
  set: (playerId: string, name: string, code: string) => {
    write(localStorage, PLAYER_ID, playerId);
    write(localStorage, PLAYER_NAME, name);
    write(localStorage, PLAYER_ROOM, code);
  },
  clearSeat: () => {
    write(localStorage, PLAYER_ID, null);
    write(localStorage, PLAYER_ROOM, null);
  },
};

/** Display token is per-tab: a second TV tab should not steal the room. */
export const displayIdentity = {
  get: () => ({
    displayToken: read(sessionStorage, DISPLAY_TOKEN),
    code: read(sessionStorage, DISPLAY_CODE),
  }),
  set: (code: string, displayToken: string) => {
    write(sessionStorage, DISPLAY_CODE, code);
    write(sessionStorage, DISPLAY_TOKEN, displayToken);
  },
  clear: () => {
    write(sessionStorage, DISPLAY_CODE, null);
    write(sessionStorage, DISPLAY_TOKEN, null);
  },
};

/** Casino pairing for a TV tab: the venue directory or one pinned table. */
export const casinoDisplayIdentity = {
  get: () => ({
    casino: read(sessionStorage, DISPLAY_CASINO),
    table: read(sessionStorage, DISPLAY_TABLE),
  }),
  set: (casino: string | null, table: string | null = null) => {
    write(sessionStorage, DISPLAY_CASINO, casino);
    write(sessionStorage, DISPLAY_TABLE, table);
  },
  clear: () => {
    write(sessionStorage, DISPLAY_CASINO, null);
    write(sessionStorage, DISPLAY_TABLE, null);
  },
};

/**
 * A member's long-lived identity inside one casino: chips and stats live on
 * the membership, so both halves are kept per casino, forever (localStorage).
 * The id is the lookup key the server knows the membership by; the token is
 * the secret that proves the membership is this phone's. The server issues
 * both — a phone that keeps only the id can no longer spend those chips.
 */
const memberKey = (casinoCode: string) => `tvcasino.member.${casinoCode.toUpperCase()}`;
const memberTokenKey = (casinoCode: string) => `tvcasino.memberToken.${casinoCode.toUpperCase()}`;

export interface StoredMembership {
  id: string;
  /** Null for a membership opened before tokens, and for the host's own seat. */
  token: string | null;
}

export const memberIdentity = {
  get: (casinoCode: string): StoredMembership | null => {
    const id = read(localStorage, memberKey(casinoCode));
    return id ? { id, token: read(localStorage, memberTokenKey(casinoCode)) } : null;
  },
  /**
   * A token is only ever written when the server just issued one. A bare
   * TV's venue (`remember: false`) closes with its table, so it never
   * becomes the last casino a typed code falls back on.
   */
  set: (casinoCode: string, { id, token }: StoredMembership, { remember = true } = {}) => {
    write(localStorage, memberKey(casinoCode), id);
    if (token) write(localStorage, memberTokenKey(casinoCode), token);
    if (remember) write(localStorage, LAST_VENUE, casinoCode.toUpperCase());
  },
  /**
   * The casino this phone last sat down in. A table code typed with no
   * casino link presents that membership; the server ignores it elsewhere.
   */
  lastVenue: (): string | null => read(localStorage, LAST_VENUE),
  /** Forget a membership this phone cannot prove; the next join opens a new one. */
  clear: (casinoCode: string) => {
    write(localStorage, memberKey(casinoCode), null);
    write(localStorage, memberTokenKey(casinoCode), null);
  },
};

/**
 * The stats window every market card reads, remembered across screens: a
 * phone that cut the floor to tonight opens the TV board the same way.
 */
export const statsWindowPref = {
  get: (): string | null => read(localStorage, STATS_WINDOW),
  set: (window: string) => write(localStorage, STATS_WINDOW, window),
};

/**
 * The referral code this browser arrived on, held until it signs up. The
 * first link wins: a later `?ref=` does not take the credit from the owner
 * who brought this browser here, and a sign-up spends it.
 */
export const referralRef = {
  get: (): string | null => read(localStorage, REFERRAL),
  capture: (search: string) => {
    const raw = new URLSearchParams(search).get('ref');
    if (!raw || read(localStorage, REFERRAL)) return;
    const code = normalizeReferralCode(raw);
    if (isReferralCode(code)) write(localStorage, REFERRAL, code);
  },
  clear: () => write(localStorage, REFERRAL, null),
};
